import {
  type SecondFactorMethod,
  useGetMe,
  useSendStaffEmailCode,
  useStepUpStaff,
} from '@postifoto/api-client';
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Alert, CodeInput, Field } from '../components/ui/input';
import { useErrorMessage } from '../i18n/hooks';
import { isProblem, PROBLEM_TYPES } from '../lib/api-errors';
import { AuthLink } from '../routes/auth-layout';
import { useResendCountdown } from '../routes/login';

type Run = <T>(action: () => Promise<T>) => Promise<T>;

const StepUpContext = createContext<Run | null>(null);

/**
 * Опасные действия (сброс 2FA, деактивация, изменения роли Администратора) требуют кода
 * не старше 5 минут. Если API ответил step-up-required, показываем окно ввода кода,
 * подтверждаем и повторяем действие. Способ по умолчанию — код из письма (ADR-067): окно
 * отправляет его само; у кого подключено приложение — переключение на его код.
 */
export function StepUpProvider({ children }: { children: ReactNode }) {
  const errorMessage = useErrorMessage();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const pending = useRef<{ resolve: () => void; reject: (error: unknown) => void } | null>(null);
  const stepUp = useStepUpStaff();
  const send = useSendStaffEmailCode();
  const me = useGetMe();
  const methods = me.data?.mfa.methods ?? [];
  const [method, setMethod] = useState<SecondFactorMethod>('totp');
  const [wait, setWait] = useResendCountdown(0);

  const sendCode = () =>
    send.mutate(undefined, {
      onSuccess: (result) => setWait(result.resendAfterSeconds),
      // Код уже отправлен недавно — он и действует.
      onError: (error) => {
        if (isProblem(error, PROBLEM_TYPES.rateLimited)) send.reset();
      },
    });
  // `run` стабилен для потребителей контекста — свежие способы и отправка берутся из ref.
  const latest = useRef({ methods, sendCode, reset: () => {} });
  latest.current = {
    methods,
    sendCode,
    reset: () => {
      stepUp.reset();
      send.reset();
    },
  };

  const run: Run = useCallback(async (action) => {
    try {
      return await action();
    } catch (error) {
      if (!isProblem(error, PROBLEM_TYPES.stepUpRequired)) throw error;
      await new Promise<void>((resolve, reject) => {
        pending.current = { resolve, reject };
        const first = latest.current.methods[0] ?? 'totp';
        setMethod(first);
        setCode('');
        latest.current.reset();
        if (first === 'email') latest.current.sendCode();
        setOpen(true);
      });
      return action();
    }
  }, []);

  const cancel = () => {
    setOpen(false);
    pending.current?.reject(new Error('step-up cancelled'));
    pending.current = null;
  };

  const switchTo = (next: SecondFactorMethod) => {
    setMethod(next);
    setCode('');
    stepUp.reset();
    if (next === 'email' && wait === 0) sendCode();
  };

  const confirm = () =>
    stepUp.mutate(
      { data: method === 'email' ? { emailCode: code } : { code } },
      {
        onSuccess: () => {
          setOpen(false);
          pending.current?.resolve();
          pending.current = null;
        },
      },
    );

  const t = useTranslations();
  return (
    <StepUpContext.Provider value={run}>
      {children}
      <Dialog
        open={open}
        title={t('stepUp.title')}
        description={
          method === 'email'
            ? t('stepUp.emailIntro', { email: me.data?.mfa.emailHint ?? '' })
            : t('stepUp.intro')
        }
        onClose={cancel}
        footer={
          <>
            <Button variant="secondary" onClick={cancel}>
              {t('common.cancel')}
            </Button>
            <Button onClick={confirm} busy={stepUp.isPending} disabled={code.length !== 6}>
              {t('stepUp.confirm')}
            </Button>
          </>
        }
      >
        <Field label={t('stepUp.code')}>
          <CodeInput
            value={code}
            onValueChange={setCode}
            onKeyDown={(event) => event.key === 'Enter' && code.length === 6 && confirm()}
          />
        </Field>
        <div className="grid gap-2">
          {method === 'email' && (
            <AuthLink onClick={sendCode} disabled={wait > 0 || send.isPending}>
              {wait > 0 ? t('login.resendIn', { seconds: wait }) : t('login.resend')}
            </AuthLink>
          )}
          {method === 'email' && methods.includes('totp') && (
            <AuthLink onClick={() => switchTo('totp')}>{t('login.useCode')}</AuthLink>
          )}
          {method === 'totp' && methods.includes('email') && (
            <AuthLink onClick={() => switchTo('email')}>{t('login.useEmail')}</AuthLink>
          )}
        </div>
        {send.error && <Alert>{errorMessage(send.error)}</Alert>}
        {stepUp.error && <Alert>{errorMessage(stepUp.error)}</Alert>}
      </Dialog>
    </StepUpContext.Provider>
  );
}

export function useStepUp(): Run {
  const run = useContext(StepUpContext);
  if (!run) throw new Error('useStepUp outside StepUpProvider');
  return run;
}
